import type { EncodedScreenshot } from './types'

const MAX_SCREENSHOT_BYTES = 950_000
const WEBP_QUALITIES = [0.82, 0.68, 0.54, 0.42]
const MAX_DOWNSCALE_STEPS = 5
const DOWNSCALE_FACTOR = 0.82

function canvasToBlob(
  canvas: HTMLCanvasElement,
  contentType: 'image/webp' | 'image/jpeg',
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('Der Screenshot konnte nicht als Bild gespeichert werden.'))
      },
      contentType,
      quality,
    )
  })
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  const chunkSize = 0x8000
  let binary = ''

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize))
  }

  return btoa(binary)
}

function downscaleCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(source.width * DOWNSCALE_FACTOR))
  canvas.height = Math.max(1, Math.round(source.height * DOWNSCALE_FACTOR))
  const context = canvas.getContext('2d')
  if (!context)
    throw new Error('Der Screenshot kann in diesem Browser nicht verarbeitet werden.')
  context.drawImage(source, 0, 0, canvas.width, canvas.height)
  return canvas
}

/** Komprimiert begrenzt und deterministisch unter das serverseitige 1-MB-Limit. */
export async function encodeScreenshot(
  source: HTMLCanvasElement,
): Promise<EncodedScreenshot> {
  let canvas = source

  for (let step = 0; step <= MAX_DOWNSCALE_STEPS; step += 1) {
    for (const quality of WEBP_QUALITIES) {
      const blob = await canvasToBlob(canvas, 'image/webp', quality)
      if (blob.size <= MAX_SCREENSHOT_BYTES) {
        return {
          contentType: 'image/webp',
          datenBase64: await blobToBase64(blob),
          breite: canvas.width,
          hoehe: canvas.height,
        }
      }
    }

    if (step < MAX_DOWNSCALE_STEPS) canvas = downscaleCanvas(canvas)
  }

  throw new Error('Der Screenshot ist trotz Komprimierung größer als 1 MB.')
}
